import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { Article } from "@/mocks/articles";

/* ─── DB row → Article mapper ─── */
function rowToArticle(row: Record<string, unknown>): Article {
  return {
    id: row.id as string,
    title: row.title as string,
    subtitle: row.subtitle as string | undefined,
    slug: row.slug as string,
    excerpt: row.excerpt as string,
    content: row.content as string,
    category: row.category as string,
    categorySlug: row.category_slug as string,
    subcategory: row.subcategory as string | undefined,
    author: row.author as string,
    authorSlug: row.author_slug as string,
    authorRole: row.author_role as string | undefined,
    authorBio: row.author_bio as string | undefined,
    authorImage: row.author_image as string | undefined,
    imageUrl: row.image_url as string,
    imageAlt: row.image_alt as string | undefined,
    gallery: row.gallery as string[] | undefined,
    publishedAt: row.published_at as string,
    updatedAt: row.updated_at as string | undefined,
    readTime: row.read_time as string,
    views: row.views as number,
    tags: (row.tags as string[]) || [],
    status: row.status as Article["status"],
    isFeatured: row.is_featured as boolean,
    isBreaking: row.is_breaking as boolean,
    isTopNews: row.is_top_news as boolean,
    isOpinion: row.is_opinion as boolean,
    isColumn: row.is_column as boolean,
    showOnHomepageSlider: row.show_on_homepage_slider as boolean,
    showInTopNewsColumn: row.show_in_top_news_column as boolean,
    showInCategoryFeatured: row.show_in_category_featured as boolean,
    commentsEnabled: row.comments_enabled as boolean,
    adsEnabled: row.ads_enabled as boolean,
    sources: row.sources as Article["sources"],
    internalLinks: row.internal_links as Article["internalLinks"],
    externalLinks: row.external_links as Article["externalLinks"],
    relatedArticleIds: row.related_article_ids as string[] | undefined,
    metaTitle: row.meta_title as string | undefined,
    metaDescription: row.meta_description as string | undefined,
    focusKeyword: row.focus_keyword as string | undefined,
    canonicalUrl: row.canonical_url as string | undefined,
    ogTitle: row.og_title as string | undefined,
    ogDescription: row.og_description as string | undefined,
    ogImage: row.og_image as string | undefined,
    twitterImage: row.twitter_image as string | undefined,
    noIndex: row.no_index as boolean,
  };
}

/* ─── Article → DB row mapper ─── */
export function articleToRow(article: Partial<Article>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (article.title !== undefined) row.title = article.title;
  if (article.subtitle !== undefined) row.subtitle = article.subtitle;
  if (article.slug !== undefined) row.slug = article.slug;
  if (article.excerpt !== undefined) row.excerpt = article.excerpt;
  if (article.content !== undefined) row.content = article.content;
  if (article.category !== undefined) row.category = article.category;
  if (article.categorySlug !== undefined) row.category_slug = article.categorySlug;
  if (article.subcategory !== undefined) row.subcategory = article.subcategory;
  if (article.author !== undefined) row.author = article.author;
  if (article.authorSlug !== undefined) row.author_slug = article.authorSlug;
  if (article.authorRole !== undefined) row.author_role = article.authorRole;
  if (article.authorBio !== undefined) row.author_bio = article.authorBio;
  if (article.authorImage !== undefined) row.author_image = article.authorImage;
  if (article.imageUrl !== undefined) row.image_url = article.imageUrl;
  if (article.imageAlt !== undefined) row.image_alt = article.imageAlt;
  if (article.gallery !== undefined) row.gallery = article.gallery;
  if (article.publishedAt !== undefined) row.published_at = article.publishedAt;
  if (article.updatedAt !== undefined) row.updated_at = article.updatedAt;
  if (article.readTime !== undefined) row.read_time = article.readTime;
  if (article.views !== undefined) row.views = article.views;
  if (article.tags !== undefined) row.tags = article.tags;
  if (article.status !== undefined) row.status = article.status;
  if (article.isFeatured !== undefined) row.is_featured = article.isFeatured;
  if (article.isBreaking !== undefined) row.is_breaking = article.isBreaking;
  if (article.isTopNews !== undefined) row.is_top_news = article.isTopNews;
  if (article.isOpinion !== undefined) row.is_opinion = article.isOpinion;
  if (article.isColumn !== undefined) row.is_column = article.isColumn;
  if (article.showOnHomepageSlider !== undefined) row.show_on_homepage_slider = article.showOnHomepageSlider;
  if (article.showInTopNewsColumn !== undefined) row.show_in_top_news_column = article.showInTopNewsColumn;
  if (article.showInCategoryFeatured !== undefined) row.show_in_category_featured = article.showInCategoryFeatured;
  if (article.commentsEnabled !== undefined) row.comments_enabled = article.commentsEnabled;
  if (article.adsEnabled !== undefined) row.ads_enabled = article.adsEnabled;
  if (article.sources !== undefined) row.sources = article.sources;
  if (article.internalLinks !== undefined) row.internal_links = article.internalLinks;
  if (article.externalLinks !== undefined) row.external_links = article.externalLinks;
  if (article.relatedArticleIds !== undefined) row.related_article_ids = article.relatedArticleIds;
  if (article.metaTitle !== undefined) row.meta_title = article.metaTitle;
  if (article.metaDescription !== undefined) row.meta_description = article.metaDescription;
  if (article.focusKeyword !== undefined) row.focus_keyword = article.focusKeyword;
  if (article.canonicalUrl !== undefined) row.canonical_url = article.canonicalUrl;
  if (article.ogTitle !== undefined) row.og_title = article.ogTitle;
  if (article.ogDescription !== undefined) row.og_description = article.ogDescription;
  if (article.ogImage !== undefined) row.og_image = article.ogImage;
  if (article.twitterImage !== undefined) row.twitter_image = article.twitterImage;
  if (article.noIndex !== undefined) row.no_index = article.noIndex;
  return row;
}

/* ─── useArticlesDB hook ─── */
export function useArticlesDB() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: dbError } = await supabase
      .from("articles")
      .select("*")
      .order("published_at", { ascending: false });

    if (dbError) {
      setError(dbError.message);
      setArticles([]);
    } else {
      setArticles((data ?? []).map(rowToArticle));
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const createArticle = useCallback(async (article: Omit<Article, "id">): Promise<{ data: Article | null; error: string | null }> => {
    const row = articleToRow(article);
    const { data, error: dbError } = await supabase
      .from("articles")
      .insert(row)
      .select()
      .maybeSingle();

    if (dbError) {
      return { data: null, error: dbError.message };
    }
    if (data) {
      const newArticle = rowToArticle(data as Record<string, unknown>);
      await fetchArticles();
      return { data: newArticle, error: null };
    }
    return { data: null, error: "Unbekannter Fehler beim Erstellen." };
  }, [fetchArticles]);

  const updateArticle = useCallback(async (id: string, updates: Partial<Article>): Promise<{ error: string | null }> => {
    const row = articleToRow(updates);
    const { error: dbError } = await supabase
      .from("articles")
      .update(row)
      .eq("id", id);

    if (dbError) {
      return { error: dbError.message };
    }
    await fetchArticles();
    return { error: null };
  }, [fetchArticles]);

  const deleteArticle = useCallback(async (id: string): Promise<{ error: string | null }> => {
    const { error: dbError } = await supabase
      .from("articles")
      .delete()
      .eq("id", id);

    if (dbError) {
      return { error: dbError.message };
    }
    await fetchArticles();
    return { error: null };
  }, [fetchArticles]);

  const bulkDelete = useCallback(async (ids: string[]): Promise<{ error: string | null }> => {
    const { error: dbError } = await supabase
      .from("articles")
      .delete()
      .in("id", ids);

    if (dbError) {
      return { error: dbError.message };
    }
    await fetchArticles();
    return { error: null };
  }, [fetchArticles]);

  const bulkUpdateStatus = useCallback(async (ids: string[], status: Article["status"]): Promise<{ error: string | null }> => {
    const { error: dbError } = await supabase
      .from("articles")
      .update({ status })
      .in("id", ids);

    if (dbError) {
      return { error: dbError.message };
    }
    await fetchArticles();
    return { error: null };
  }, [fetchArticles]);

  return {
    articles,
    loading,
    error,
    refetch: fetchArticles,
    createArticle,
    updateArticle,
    deleteArticle,
    bulkDelete,
    bulkUpdateStatus,
  };
}

/* ─── useAuthorsDB (lightweight, for article form dropdowns) ─── */
export function useAuthorsDB() {
  const [authors, setAuthors] = useState<{ name: string; slug: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("authors")
      .select("name, slug")
      .order("name")
      .then(({ data }) => {
        setAuthors((data ?? []) as { name: string; slug: string }[]);
        setLoading(false);
      });
  }, []);

  return { authors, loading };
}
